import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { DataTable, FilterBar, KpiTile, Notice, StatusPill } from '../patterns';
import type { StatusVariant } from '../patterns';
import {
  nutritionService,
  NutritionAudit,
  NutritionAuditOrchard,
  NutritionAuditSource,
} from '../../services/nutritionService';
import './nutrition.css';

const STATUS_VARIANT: Record<string, StatusVariant> = {
  applied: 'ok',
  partial: 'warn',
  not_applied: 'warn',
};

const fmt = (v: number | null): string => (v == null ? '—' : String(v));

const NutritionAuditPage: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<NutritionAudit | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [source, setSource] = useState<NutritionAuditSource>('all');
  const [perHa, setPerHa] = useState(true);
  const [search, setSearch] = useState('');
  // The first response seeds `from`/`to` from `selectedFrom`/`selectedTo`. That
  // state change would re-run this effect and fire a redundant second fetch for
  // the same range; `justSeededRange` skips exactly that one follow-up run so a
  // fresh load (and a source toggle made before the user picks a range) fetches
  // once.
  const justSeededRange = useRef(false);

  useEffect(() => {
    if (justSeededRange.current) {
      justSeededRange.current = false;
      return;
    }
    nutritionService
      .getAudit({ from: from || undefined, to: to || undefined, source })
      .then((d) => {
        setData(d);
        if (!from && !to) {
          justSeededRange.current = true;
          setFrom(d.selectedFrom);
          setTo(d.selectedTo);
        }
      })
      .catch(() => setData(null));
  }, [from, to, source]);

  // Keep the range ordered: editing one bound past the other drags the other along.
  const onFromChange = (v: string) => {
    setFrom(v);
    if (to && v > to) setTo(v);
  };
  const onToChange = (v: string) => {
    setTo(v);
    if (from && v < from) setFrom(v);
  };

  const rows = useMemo(() => {
    const all = data?.orchards ?? [];
    const q = search.trim().toLowerCase();
    return q ? all.filter((o) => o.orchardNo.toLowerCase().includes(q)) : all;
  }, [data, search]);

  const cell = (o: NutritionAuditOrchard, key: 'n' | 'p' | 'k'): string =>
    perHa ? fmt(o[`${key}PerHa` as const]) : fmt(o[key]);

  const handleExport = async () => {
    const blob = await nutritionService.exportAudit({ from: from || undefined, to: to || undefined, source });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const range =
      data && data.selectedFrom !== data.selectedTo
        ? `${data.selectedFrom}_${data.selectedTo}`
        : data?.selectedFrom ?? 'export';
    a.download = `nutrition-audit-${range}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const chips = [
    { id: 'view:perHa', label: t('nutrition.audit.perHa'), active: perHa },
    { id: 'view:total', label: t('nutrition.audit.perOrchard'), active: !perHa },
    { id: 'src:all', label: t('nutrition.audit.source.all'), active: source === 'all' },
    { id: 'src:soil', label: t('nutrition.audit.source.soil'), active: source === 'soil' },
    { id: 'src:foliar', label: t('nutrition.audit.source.foliar'), active: source === 'foliar' },
  ];
  const onChip = (id: string) => {
    if (id === 'view:perHa') setPerHa(true);
    else if (id === 'view:total') setPerHa(false);
    else if (id.startsWith('src:')) setSource(id.slice(4) as NutritionAuditSource);
  };

  const season = data?.season;

  return (
    <div className="ntr-page" data-testid="nutrition-audit">
      <PageHeader
        title={t('nutrition.audit.title')}
        meta={t('nutrition.audit.subtitle')}
        actions={
          <button type="button" className="pat-btn pat-btn--primary" onClick={handleExport}>
            {t('nutrition.audit.export')}
          </button>
        }
      />

      <div
        className="ntr-card"
        style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
      >
        <label className="ntr-field" style={{ marginBottom: 0 }}>
          <span>{t('nutrition.audit.from')}</span>
          <input type="month" value={from} onChange={(e) => onFromChange(e.target.value)} />
        </label>
        <label className="ntr-field" style={{ marginBottom: 0 }}>
          <span>{t('nutrition.audit.to')}</span>
          <input type="month" value={to} onChange={(e) => onToChange(e.target.value)} />
        </label>
      </div>

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('nutrition.audit.searchPlaceholder')}
        chips={chips}
        onChipClick={onChip}
      />

      {season && (
        <div className="ntr-section-label">{t('nutrition.audit.season.heading')}</div>
      )}
      {season && (
        <div className="pat-kpi-row">
          <KpiTile
            label={t('nutrition.audit.columns.n')}
            value={`${fmt(season.nPerHa)} ${t('nutrition.audit.season.perHa')}`}
            delta={`${t('nutrition.audit.season.total')}: ${fmt(season.n)}`}
          />
          <KpiTile
            label={t('nutrition.audit.columns.p')}
            value={`${fmt(season.pPerHa)} ${t('nutrition.audit.season.perHa')}`}
            delta={`${t('nutrition.audit.season.total')}: ${fmt(season.p)}`}
          />
          <KpiTile
            label={t('nutrition.audit.columns.k')}
            value={`${fmt(season.kPerHa)} ${t('nutrition.audit.season.perHa')}`}
            delta={`${t('nutrition.audit.season.total')}: ${fmt(season.k)}`}
          />
        </div>
      )}

      {data && data.needsLinking > 0 && (
        <Notice tone="warn">
          {t('nutrition.audit.needsLinking', { count: data.needsLinking })}
        </Notice>
      )}

      <DataTable<NutritionAuditOrchard>
        rows={rows}
        rowKey={(o) => o.orchardId}
        columns={[
          {
            key: 'orchard',
            label: t('nutrition.audit.columns.orchard'),
            render: (o) => <strong>{o.orchardNo}</strong>,
          },
          {
            key: 'ha',
            label: t('nutrition.audit.columns.ha'),
            align: 'right',
            render: (o) => String(o.ha),
          },
          {
            key: 'n',
            label: t('nutrition.audit.columns.n'),
            align: 'right',
            render: (o) => cell(o, 'n'),
          },
          {
            key: 'p',
            label: t('nutrition.audit.columns.p'),
            align: 'right',
            render: (o) => cell(o, 'p'),
          },
          {
            key: 'k',
            label: t('nutrition.audit.columns.k'),
            align: 'right',
            render: (o) => cell(o, 'k'),
          },
          {
            key: 'status',
            label: t('nutrition.audit.columns.status'),
            render: (o) => (
              <StatusPill variant={STATUS_VARIANT[o.status] || 'warn'}>
                {t(`nutrition.audit.status.${o.status}`)}
              </StatusPill>
            ),
          },
        ]}
        emptyState={t('nutrition.audit.empty')}
        mobileCardRow={(o) => (
          <div>
            <strong>{o.orchardNo}</strong> · {o.cultivar}
            <div style={{ fontSize: 11, color: 'var(--shell-text-muted)' }}>
              N {cell(o, 'n')} · P {cell(o, 'p')} · K {cell(o, 'k')}
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default NutritionAuditPage;
